import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AttendanceDayTimes,
  DEFAULT_DAY_TIMES,
  DEFAULT_HOLIDAY_WEEKDAYS,
  DEFAULT_STUDENT_CARD_COLORS,
  SchoolSettings,
  SchoolSettingsService,
} from 'src/app/services/school-settings.service';

type DayTimesGroup = FormGroup<{
  checkInOpen: FormControl<string>;
  checkInLateAfter: FormControl<string>;
  checkOutOpen: FormControl<string>;
  checkOutLateAfter: FormControl<string>;
}>;

@Component({
  selector: 'app-school-settings',
  templateUrl: './school-settings.component.html',
  styleUrls: ['./school-settings.component.css']
})
export class SchoolSettingsComponent {
  loading = false;
  saving = false;
  isError = false;
  errorMessage = '';
  successMessage = '';

  readonly weekdays = [0, 1, 2, 3, 4, 5, 6];

  form = new FormGroup({
    schoolName: new FormControl('', [Validators.required]),
    departmentName: new FormControl(''),
    headName: new FormControl(''),
    sectionHeads: new FormArray<FormGroup<{ name: FormControl<string> }>>([]),
    studentCardColors: new FormGroup({
      busBackground: new FormControl(DEFAULT_STUDENT_CARD_COLORS.busBackground, { nonNullable: true }),
      noBusBackground: new FormControl(DEFAULT_STUDENT_CARD_COLORS.noBusBackground, { nonNullable: true }),
      cutMarginMm: new FormControl(DEFAULT_STUDENT_CARD_COLORS.cutMarginMm, { nonNullable: true }),
    }),
    attendanceSchedule: new FormGroup({
      checkInOpen: new FormControl(DEFAULT_DAY_TIMES.checkInOpen, { nonNullable: true }),
      checkInLateAfter: new FormControl(DEFAULT_DAY_TIMES.checkInLateAfter, { nonNullable: true }),
      checkOutOpen: new FormControl(DEFAULT_DAY_TIMES.checkOutOpen, { nonNullable: true }),
      checkOutLateAfter: new FormControl(DEFAULT_DAY_TIMES.checkOutLateAfter, { nonNullable: true }),
      lateEnabled: new FormControl(true, { nonNullable: true }),
      holidayWeekdays: new FormControl<number[]>([...DEFAULT_HOLIDAY_WEEKDAYS], { nonNullable: true }),
      holidayDates: new FormArray<FormGroup<{
        name: FormControl<string>;
        date: FormControl<string>;
      }>>([]),
      byWeekday: new FormGroup({
        '0': this.createDayTimesGroup(),
        '1': this.createDayTimesGroup(),
        '2': this.createDayTimesGroup(),
        '3': this.createDayTimesGroup(),
        '4': this.createDayTimesGroup(),
        '5': this.createDayTimesGroup(),
        '6': this.createDayTimesGroup(),
      }),
    }),
  });

  constructor(private schoolSettingsService: SchoolSettingsService) { }

  get sectionHeads(): FormArray<FormGroup<{ name: FormControl<string> }>> {
    return this.form.controls.sectionHeads;
  }

  get attendanceSchedule(): FormGroup {
    return this.form.controls.attendanceSchedule;
  }

  get byWeekday(): FormGroup {
    return this.attendanceSchedule.controls['byWeekday'] as FormGroup;
  }

  get holidayDates(): FormArray<FormGroup<{ name: FormControl<string>; date: FormControl<string> }>> {
    return this.attendanceSchedule.controls['holidayDates'] as FormArray<FormGroup<{
      name: FormControl<string>;
      date: FormControl<string>;
    }>>;
  }

  get holidayWeekdays(): number[] {
    return this.attendanceSchedule.controls['holidayWeekdays'].value || [];
  }

  get lateEnabled(): boolean {
    return this.attendanceSchedule.controls['lateEnabled'].value !== false;
  }

  async ngOnInit() {
    await this.loadSettings();
  }

  onLateEnabledChange(): void {
    this.syncLateFieldsEnabled();
  }

  dayTimesGroup(weekday: number): DayTimesGroup {
    return this.byWeekday.controls[String(weekday)] as DayTimesGroup;
  }

  isHoliday(weekday: number): boolean {
    return this.holidayWeekdays.includes(weekday);
  }

  toggleHoliday(weekday: number, checked: boolean): void {
    const current = new Set(this.holidayWeekdays);
    if (checked) {
      current.add(weekday);
    } else {
      current.delete(weekday);
    }
    this.attendanceSchedule.controls['holidayWeekdays'].setValue(
      [...current].sort((a, b) => a - b)
    );
    this.syncDayEnabledState(weekday);
  }

  private syncDayEnabledState(weekday?: number): void {
    const days = weekday === undefined ? this.weekdays : [weekday];
    for (const day of days) {
      const group = this.dayTimesGroup(day);
      if (this.isHoliday(day)) {
        group.disable({ emitEvent: false });
      } else {
        group.enable({ emitEvent: false });
        if (!this.lateEnabled) {
          group.controls.checkInLateAfter.disable({ emitEvent: false });
          group.controls.checkOutLateAfter.disable({ emitEvent: false });
        }
      }
    }
    this.syncLateFieldsEnabled();
  }

  private syncLateFieldsEnabled(): void {
    const defaultLateIn = this.attendanceSchedule.controls['checkInLateAfter'];
    const defaultLateOut = this.attendanceSchedule.controls['checkOutLateAfter'];
    if (this.lateEnabled) {
      defaultLateIn.enable({ emitEvent: false });
      defaultLateOut.enable({ emitEvent: false });
    } else {
      defaultLateIn.disable({ emitEvent: false });
      defaultLateOut.disable({ emitEvent: false });
    }
    for (const day of this.weekdays) {
      if (this.isHoliday(day)) {
        continue;
      }
      const group = this.dayTimesGroup(day);
      if (this.lateEnabled) {
        group.controls.checkInLateAfter.enable({ emitEvent: false });
        group.controls.checkOutLateAfter.enable({ emitEvent: false });
      } else {
        group.controls.checkInLateAfter.disable({ emitEvent: false });
        group.controls.checkOutLateAfter.disable({ emitEvent: false });
      }
    }
  }

  applyDefaultsToSchoolDays(): void {
    const defaults = this.attendanceSchedule.getRawValue() as AttendanceDayTimes & {
      holidayWeekdays: number[];
      byWeekday: Record<string, AttendanceDayTimes>;
    };
    for (const weekday of this.weekdays) {
      if (this.isHoliday(weekday)) {
        continue;
      }
      this.dayTimesGroup(weekday).patchValue({
        checkInOpen: defaults.checkInOpen,
        checkInLateAfter: defaults.checkInLateAfter,
        checkOutOpen: defaults.checkOutOpen,
        checkOutLateAfter: defaults.checkOutLateAfter,
      });
    }
  }

  async loadSettings() {
    this.loading = true;
    this.isError = false;
    this.successMessage = '';
    try {
      const settings = await this.schoolSettingsService.get();
      this.patchForm(settings);
    } catch (error: any) {
      this.showError(error?.error?.errorMessages?.[0]?.message || 'SCHOOL.LOAD_FAILED');
    } finally {
      this.loading = false;
    }
  }

  private patchForm(settings: SchoolSettings) {
    const schedule = settings.attendanceSchedule;
    const defaults: AttendanceDayTimes = {
      checkInOpen: schedule?.checkInOpen || DEFAULT_DAY_TIMES.checkInOpen,
      checkInLateAfter: schedule?.checkInLateAfter || DEFAULT_DAY_TIMES.checkInLateAfter,
      checkOutOpen: schedule?.checkOutOpen || DEFAULT_DAY_TIMES.checkOutOpen,
      checkOutLateAfter: schedule?.checkOutLateAfter || DEFAULT_DAY_TIMES.checkOutLateAfter,
    };
    const holidays = Array.isArray(schedule?.holidayWeekdays) && schedule.holidayWeekdays.length
      ? [...schedule.holidayWeekdays]
      : [...DEFAULT_HOLIDAY_WEEKDAYS];

    this.form.patchValue({
      schoolName: settings.schoolName || '',
      departmentName: settings.departmentName || '',
      headName: settings.headName || '',
      studentCardColors: {
        busBackground: settings.studentCardColors?.busBackground || DEFAULT_STUDENT_CARD_COLORS.busBackground,
        noBusBackground: settings.studentCardColors?.noBusBackground || DEFAULT_STUDENT_CARD_COLORS.noBusBackground,
        cutMarginMm: settings.studentCardColors?.cutMarginMm ?? DEFAULT_STUDENT_CARD_COLORS.cutMarginMm,
      },
      attendanceSchedule: {
        ...defaults,
        lateEnabled: schedule?.lateEnabled !== false,
        holidayWeekdays: holidays,
      },
    });

    for (const weekday of this.weekdays) {
      const key = String(weekday);
      const day = schedule?.byWeekday?.[key] || defaults;
      this.dayTimesGroup(weekday).patchValue({
        checkInOpen: day.checkInOpen || defaults.checkInOpen,
        checkInLateAfter: day.checkInLateAfter || defaults.checkInLateAfter,
        checkOutOpen: day.checkOutOpen || defaults.checkOutOpen,
        checkOutLateAfter: day.checkOutLateAfter || defaults.checkOutLateAfter,
      });
    }

    this.sectionHeads.clear();
    for (const sectionHead of settings.sectionHeads || []) {
      this.sectionHeads.push(this.createSectionHeadGroup(sectionHead.name));
    }

    this.holidayDates.clear();
    for (const holiday of schedule?.holidayDates || []) {
      this.holidayDates.push(this.createHolidayDateGroup(holiday.name, holiday.date));
    }

    this.syncDayEnabledState();
  }

  private createDayTimesGroup(times: AttendanceDayTimes = DEFAULT_DAY_TIMES): DayTimesGroup {
    return new FormGroup({
      checkInOpen: new FormControl(times.checkInOpen, { nonNullable: true }),
      checkInLateAfter: new FormControl(times.checkInLateAfter, { nonNullable: true }),
      checkOutOpen: new FormControl(times.checkOutOpen, { nonNullable: true }),
      checkOutLateAfter: new FormControl(times.checkOutLateAfter, { nonNullable: true }),
    });
  }

  private createSectionHeadGroup(name = '') {
    return new FormGroup({
      name: new FormControl(name, { nonNullable: true, validators: [Validators.required] }),
    });
  }

  private createHolidayDateGroup(name = '', date = '') {
    return new FormGroup({
      name: new FormControl(name, { nonNullable: true, validators: [Validators.required] }),
      date: new FormControl(date, { nonNullable: true, validators: [Validators.required] }),
    });
  }

  addSectionHead() {
    this.sectionHeads.push(this.createSectionHeadGroup());
    setTimeout(() => {
      const index = this.sectionHeads.length - 1;
      document.getElementById(`sectionHead${index}`)?.focus();
    });
  }

  removeSectionHead(index: number) {
    this.sectionHeads.removeAt(index);
  }

  addHolidayDate() {
    this.holidayDates.push(this.createHolidayDateGroup());
    setTimeout(() => {
      const index = this.holidayDates.length - 1;
      document.getElementById(`holidayName${index}`)?.focus();
    });
  }

  removeHolidayDate(index: number) {
    this.holidayDates.removeAt(index);
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showError('COMMON.REQUIRED_FIELDS');
      return;
    }

    this.saving = true;
    this.isError = false;
    this.successMessage = '';
    try {
      const value = this.form.getRawValue();
      const schedule = value.attendanceSchedule;
      await this.schoolSettingsService.update({
        schoolName: value.schoolName || '',
        departmentName: value.departmentName || '',
        headName: value.headName || '',
        sectionHeads: (value.sectionHeads || [])
          .map(item => ({ name: item.name.trim() }))
          .filter(item => item.name),
        studentCardColors: {
          busBackground: value.studentCardColors?.busBackground || DEFAULT_STUDENT_CARD_COLORS.busBackground,
          noBusBackground: value.studentCardColors?.noBusBackground || DEFAULT_STUDENT_CARD_COLORS.noBusBackground,
          cutMarginMm: value.studentCardColors?.cutMarginMm ?? DEFAULT_STUDENT_CARD_COLORS.cutMarginMm,
        },
        attendanceSchedule: {
          checkInOpen: schedule.checkInOpen,
          checkInLateAfter: schedule.checkInLateAfter,
          checkOutOpen: schedule.checkOutOpen,
          checkOutLateAfter: schedule.checkOutLateAfter,
          lateEnabled: schedule.lateEnabled !== false,
          holidayWeekdays: schedule.holidayWeekdays || [],
          holidayDates: (schedule.holidayDates || [])
            .map(item => ({
              name: (item.name || '').trim(),
              date: (item.date || '').trim(),
            }))
            .filter(item => item.name && item.date),
          byWeekday: {
            '0': schedule.byWeekday['0'],
            '1': schedule.byWeekday['1'],
            '2': schedule.byWeekday['2'],
            '3': schedule.byWeekday['3'],
            '4': schedule.byWeekday['4'],
            '5': schedule.byWeekday['5'],
            '6': schedule.byWeekday['6'],
          },
        },
      });
      this.successMessage = 'SCHOOL.SAVED';
    } catch (error: any) {
      this.showError(error?.error?.errorMessages?.[0]?.message || 'SCHOOL.SAVE_FAILED');
    } finally {
      this.saving = false;
    }
  }

  showError(message: string) {
    this.isError = true;
    this.errorMessage = message;
    this.successMessage = '';
  }
}
